import { toPlainDate } from '@/lib/format'
import type { Labeled } from '@/lib/tone'

export type OnlineChargeMethod = 'boleto' | 'pix'
export type PaymentAccountStatus = 'onboarding' | 'verifying' | 'active' | 'restricted'
export type OnlineChargeStatus = 'creating' | 'open' | 'paid' | 'expired' | 'canceled' | 'failed'

export interface MethodFee {
  fixed: number
  percent: number
  minAmount: number
  maxAmount: number
}

export interface OnlineBillingFees {
  platformFee: number
  boleto: MethodFee
  pix: MethodFee
}

export interface PaymentAccount {
  id: string
  stripeAccountId: string
  status: PaymentAccountStatus
  chargesEnabled: boolean
  payoutsEnabled: boolean
  detailsSubmitted: boolean
  boletoStatus: string
  pixStatus: string
  requirementsDue: string[]
  disabledReason: string | null
  bankName: string | null
  bankLast4: string | null
  autoChargeEnabled: boolean
  autoChargeMethod: OnlineChargeMethod
  autoChargeDaysBefore: number
  updatedAt: string
}

export interface OnlineBillingOverview {
  account: PaymentAccount | null
  fees: OnlineBillingFees
  available: boolean
}

export interface OnlineBillingSettingsInput {
  autoChargeEnabled?: boolean
  autoChargeMethod?: OnlineChargeMethod
  autoChargeDaysBefore?: number
}

export interface ConnectedPayout {
  id: string
  amount: number
  status: string
  arrivalDate: string
  createdAt: string
}

export interface OnlineBillingBalance {
  available: number
  pending: number
  payouts: ConnectedPayout[]
}

export interface OnlineCharge {
  id: string
  paymentId: string
  method: OnlineChargeMethod
  status: OnlineChargeStatus
  trigger: 'manual' | 'auto'
  amount: number
  platformFee: number
  processingFee: number
  netAmount: number
  feesConfirmed: boolean
  boletoNumber: string | null
  boletoPdfUrl: string | null
  hostedUrl: string | null
  pixCode: string | null
  pixQrCodeUrl: string | null
  expiresAt: string | null
  paidAt: string | null
  errorCode: string | null
  createdAt: string
}

export interface CreateOnlineChargeInput {
  method: OnlineChargeMethod
  amount?: number
  dueDate?: string
}

export const ONLINE_CHARGE_METHODS: OnlineChargeMethod[] = ['boleto', 'pix']

export const AUTO_CHARGE_DAY_OPTIONS = [3, 5, 7, 10] as const

export const ONLINE_BILLING_PATH = '/financeiro/recebimentos'

const METHOD_LABEL: Record<OnlineChargeMethod, string> = { boleto: 'Boleto', pix: 'Pix' }

export const methodLabel = (method: OnlineChargeMethod): string => METHOD_LABEL[method] ?? method

const ACCOUNT_STATUS: Record<PaymentAccountStatus, Labeled> = {
  onboarding: { label: 'Cadastro pendente', tone: 'warning' },
  verifying: { label: 'Em análise', tone: 'progress' },
  active: { label: 'Ativa', tone: 'success' },
  restricted: { label: 'Com pendências', tone: 'danger' }
}

export const accountStatus = (status?: PaymentAccountStatus | null): Labeled =>
  (status && ACCOUNT_STATUS[status]) || { label: 'Não ativada', tone: 'neutral' }

const CHARGE_STATUS: Record<OnlineChargeStatus, Labeled> = {
  creating: { label: 'Gerando', tone: 'progress' },
  open: { label: 'Aguardando pagamento', tone: 'info' },
  paid: { label: 'Pago', tone: 'success' },
  expired: { label: 'Vencido', tone: 'neutral' },
  canceled: { label: 'Cancelado', tone: 'neutral' },
  failed: { label: 'Falhou', tone: 'danger' }
}

export const chargeStatus = (status: OnlineChargeStatus): Labeled => CHARGE_STATUS[status] ?? { label: status, tone: 'neutral' }

const CAPABILITY_STATUS: Record<string, Labeled> = {
  active: { label: 'Liberado', tone: 'success' },
  pending: { label: 'Em análise', tone: 'progress' },
  inactive: { label: 'Indisponível', tone: 'neutral' },
  unrequested: { label: 'Indisponível', tone: 'neutral' }
}

export const capabilityStatus = (status?: string | null): Labeled => (status && CAPABILITY_STATUS[status]) || CAPABILITY_STATUS.inactive

export const methodEnabled = (
  account: Pick<PaymentAccount, 'chargesEnabled' | 'boletoStatus' | 'pixStatus'> | null | undefined,
  method: OnlineChargeMethod
): boolean => !!account && account.chargesEnabled && (method === 'boleto' ? account.boletoStatus : account.pixStatus) === 'active'

const roundCents = (value: number): number => Math.round(value * 100) / 100

export interface ChargeEstimate {
  amount: number
  processingFee: number
  platformFee: number
  netAmount: number
}

export function estimateCharge(fees: OnlineBillingFees, method: OnlineChargeMethod, amount: number): ChargeEstimate {
  const fee = fees[method]
  const safeAmount = Math.max(0, roundCents(amount))
  const processingFee = roundCents(fee.fixed + (safeAmount * fee.percent) / 100)
  const platformFee = Math.min(fees.platformFee, safeAmount)
  return { amount: safeAmount, processingFee, platformFee, netAmount: Math.max(0, roundCents(safeAmount - processingFee - platformFee)) }
}

const percentText = (value: number) => `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`

const moneyText = (value: number) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }).replace(/\s/g, ' ')

export function processingFeeText(fee: Pick<MethodFee, 'fixed' | 'percent'>): string {
  const parts = [fee.percent > 0 && percentText(fee.percent), fee.fixed > 0 && moneyText(fee.fixed)].filter(Boolean)
  return parts.length ? parts.join(' + ') : 'Grátis'
}

export function amountError(fees: OnlineBillingFees, method: OnlineChargeMethod, amount: number): string | null {
  const { minAmount, maxAmount } = fees[method]
  if (!Number.isFinite(amount) || amount < minAmount)
    return `O ${methodLabel(method).toLowerCase()} precisa ser de pelo menos ${moneyText(minAmount)}`
  if (amount > maxAmount) return `O ${methodLabel(method).toLowerCase()} pode ser de até ${moneyText(maxAmount)}`
  return null
}

export const PAYER_FIELD_LABEL: Record<string, string> = {
  name: 'nome',
  email: 'e-mail',
  document: 'CPF ou CNPJ',
  address: 'endereço completo com CEP'
}

export function missingPayerText(missing?: unknown): string {
  const fields = String(missing ?? '')
    .split(',')
    .map(field => PAYER_FIELD_LABEL[field.trim()])
    .filter(Boolean)
  if (fields.length === 0) return 'Complete o cadastro do locatário.'
  const list = fields.length === 1 ? fields[0] : `${fields.slice(0, -1).join(', ')} e ${fields[fields.length - 1]}`
  return `Complete o cadastro do locatário: falta ${list}.`
}

const REQUIREMENT_LABEL: Record<string, string> = {
  external_account: 'conta bancária',
  'business_profile.url': 'site da empresa',
  'business_profile.mcc': 'ramo de atividade',
  'company.tax_id': 'CNPJ',
  'company.address.line1': 'endereço da empresa',
  'company.phone': 'telefone da empresa',
  'tos_acceptance.date': 'aceite dos termos da Stripe',
  'representative.verification.document': 'documento do representante'
}

export function requirementsText(requirements: string[]): string | null {
  if (requirements.length === 0) return null
  const known = [...new Set(requirements.map(item => REQUIREMENT_LABEL[item]).filter(Boolean))]
  const others = requirements.filter(item => !REQUIREMENT_LABEL[item]).length
  const parts = [...known, ...(others > 0 ? [`${others} ${others === 1 ? 'outro dado' : 'outros dados'}`] : [])]
  return parts.join(', ')
}

const OVERDUE_GRACE_DAYS = 3

export function defaultChargeDueDate(dueDate: string, today: Date = new Date()): string {
  const due = dueDate.slice(0, 10)
  if (due >= toPlainDate(today)) return due
  return toPlainDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() + OVERDUE_GRACE_DAYS))
}

export const isChargeOpen = (charge: Pick<OnlineCharge, 'status'>) => charge.status === 'open' || charge.status === 'creating'

export const openChargeOf = (charges: OnlineCharge[]): OnlineCharge | null => charges.find(isChargeOpen) ?? null

const STRIPE_ACCOUNT_REJECTIONS: Record<string, string> = {
  account_create_activation_required: 'A Stripe ainda está liberando a Habitar para abrir contas de recebimento. Tente de novo em alguns dias.'
}

export const stripeAccountRejectionText = (stripeCode: unknown): string =>
  (typeof stripeCode === 'string' && STRIPE_ACCOUNT_REJECTIONS[stripeCode]) ||
  'A Stripe recusou o cadastro agora. Tente de novo em alguns minutos; se continuar, fale com a equipe da Habitar.'

export const ONLINE_BILLING_ERROR_MESSAGES: Record<string, string> = {
  PAYMENT_ACCOUNT_NOT_READY: 'A conta de recebimentos ainda não está liberada para cobrar. Termine o cadastro em Financeiro → Recebimentos.',
  PAYMENT_ACCOUNT_NOT_FOUND: 'Os recebimentos online ainda não foram ativados nesta imobiliária.',
  PAYMENT_METHOD_UNAVAILABLE: 'Esta forma de pagamento ainda não foi liberada pela Stripe para a sua conta.',
  ONLINE_CHARGE_ALREADY_OPEN: 'Esta parcela já tem um boleto ou Pix em aberto.',
  ONLINE_CHARGE_AMOUNT_OUT_OF_RANGE: 'O valor está fora do limite desta forma de pagamento.',
  ONLINE_CHARGE_DUE_DATE_INVALID: 'Escolha um vencimento de hoje em diante (boleto: até 60 dias).',
  ONLINE_CHARGE_NOT_CANCELABLE: 'Boleto não pode ser cancelado: ele vale até o vencimento. Só o Pix pode ser cancelado.',
  ONLINE_CHARGE_NOT_FOUND: 'Cobrança online não encontrada.',
  STRIPE_REJECTED: 'A Stripe recusou a cobrança. Confira os dados do locatário e tente de novo.',
  CONFIGURATION_ERROR: 'Os recebimentos online estão indisponíveis no momento.'
}
